import { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { fetchWarehouseOperators } from '../../../services/workforceServices'

const useDebounced = (value, delay) => {
    const [v, setV] = useState(value)
    useEffect(() => {
        const id = setTimeout(() => setV(value), delay)
        return () => clearTimeout(id)
    }, [value, delay])
    return v
}

export const ExtraShiftPicker = ({ warehouse_id, excludeIds, onPick, onClose }) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [hours, setHours] = useState('1')
    const [selected, setSelected] = useState(null)
    const debouncedQuery = useDebounced(query, 300)

    useEffect(() => { setOpen(true) }, [])

    const close = () => { setOpen(false); setTimeout(onClose, 250) }

    const { data, isFetching } = useQuery(
        ['workforce', warehouse_id, 'operators', { for_extra: true, q: debouncedQuery }],
        () => fetchWarehouseOperators(warehouse_id, { full_name_or_email: debouncedQuery, include_inactive: false }),
        { enabled: debouncedQuery.length >= 2, keepPreviousData: true }
    )
    const results = useMemo(() => {
        const raw = Array.isArray(data) ? data : (data?.warehouse_operators || data?.operators || [])
        return raw.filter((op) => !excludeIds.includes(op.id))
    }, [data, excludeIds])

    const handleConfirm = () => {
        const n = Number(hours)
        if (!selected || !n || n < 0.5 || n > 24) return
        onPick({ operator: selected, extra_hours: n })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-20 overflow-y-auto" open={open} onClose={close}>
                <div className="flex items-center justify-center min-h-screen px-4">
                    <Dialog.Overlay className="fixed inset-0 bg-gray-700 bg-opacity-40" />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium">{t('workforce.attendance.add_extra')}</Dialog.Title>
                            <button type="button" onClick={close} className="text-gray-400 hover:text-gray-500">
                                <XIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-6 py-4 space-y-3">
                            <input
                                type="text" value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null) }}
                                placeholder={t('workforce.attendance.search_operator')}
                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                            />
                            {selected ? (
                                <div className="p-2 bg-indigo-50 border border-indigo-200 rounded text-sm flex items-center justify-between">
                                    <span><strong>{selected.user_name || selected.user?.full_name || selected.full_name}</strong> <span className="text-gray-500">{selected.operator_type}</span></span>
                                    <button type="button" onClick={() => setSelected(null)} className="text-xs text-gray-500 hover:text-gray-700">{t('workforce.operators.clear')}</button>
                                </div>
                            ) : debouncedQuery.length >= 2 && (
                                <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
                                    {isFetching && <div className="p-2 text-xs text-gray-500">{t('workforce.operators.searching')}</div>}
                                    {!isFetching && results.length === 0 && <div className="p-2 text-xs text-gray-500">{t('workforce.operators.no_results')}</div>}
                                    {!isFetching && results.map((op) => (
                                        <button key={op.id} type="button" onClick={() => setSelected(op)}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                                            <div className="font-medium">{op.user_name || op.user?.full_name || op.full_name}</div>
                                            <div className="text-xs text-gray-500">{op.operator_type} · {op.contract_type}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.attendance.extra_hours')}</label>
                                <input
                                    type="number" step="0.5" min="0.5" max="24"
                                    value={hours} onChange={(e) => setHours(e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                            <button type="button" onClick={close}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                                {t('workforce.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={!selected || !hours || Number(hours) < 0.5}
                                className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {t('workforce.attendance.add')}
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
